import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { imgUrl } from "@/lib/api";
import { MapPin, ArrowRight } from "lucide-react";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.85, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Overline = ({ children }) => (
  <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{children}</p>
);

export function WorkCard({ p, index = 0 }) {
  return (
    <Reveal delay={index * 0.07} className="group">
      <article data-testid={`lavoro-card-${p.id}`} className="relative overflow-hidden border border-slate-200 bg-white shadow-sm transition-shadow duration-500 group-hover:shadow-xl">
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={imgUrl(p.image)}
            alt={p.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
          />
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5">
          {p.category && (
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">{p.category}</p>
          )}
          <h3 className="mt-1 font-serif text-xl text-white sm:text-2xl">{p.name}</h3>
          {p.location && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-white/70">
              <MapPin className="h-3 w-3" /> {p.location}
            </p>
          )}
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100" />
      </article>
    </Reveal>
  );
}

export function NewsCard({ p, index = 0 }) {
  return (
    <Reveal delay={index * 0.08} className="group">
      <Link
        to={`/lavori/novita/${p.id}`}
        data-testid={`novita-card-${p.id}`}
        className="block cursor-pointer"
      >
        <article className="relative overflow-hidden border border-slate-200 bg-white shadow-sm transition-shadow duration-500 hover:shadow-xl">
          <div className="relative aspect-[16/10] overflow-hidden">
            <img
              src={imgUrl(p.image)}
              alt={p.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />
            {p.status && (
              <span className="absolute left-4 top-4 border border-gold/40 bg-white/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-sm">
                {p.status}
              </span>
            )}
          </div>
          <div className="p-5">
            <h3 className="font-serif text-xl text-slate-900 sm:text-2xl">{p.name}</h3>
            {p.location && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                <MapPin className="h-3 w-3 text-gold" /> {p.location}
              </p>
            )}
            {p.description && <p className="mt-2 text-sm leading-relaxed text-slate-500 line-clamp-2">{p.description}</p>}
            <p className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
              Apri la scheda <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
            </p>
          </div>
        </article>
      </Link>
    </Reveal>
  );
}
